import { useEffect, useMemo, useState } from 'react';
import { useCiAuthStore } from '../auth/ciAuthStore';
import { useCiPoolStore } from '../authorization/ciPoolStore';
import {
  fetchActivityTimeseries,
  fetchEngagementComparison,
  fetchPlayerOwnership,
  fetchRosterInjuryNews,
  fetchSigningPlayerComparison,
  fetchTopPlayers,
  isAuthorizationError,
} from '../data/ciApi';
import type {
  ActivityDayPoint,
  AuthorizedPool,
  EngagementComparison,
  PlayerOwnership,
  RosterInjuryNews,
  SigningPlayerComparison,
  TopPlayer,
  TimeWindow,
} from '../data/types';
import { ROSTER_MOVES_TRACKED_SINCE, TIME_WINDOW_DAYS } from '../data/types';
import { windowDays } from '../data/metrics';
import { PERIOD_PHRASES } from '../data/overviewStory';
import { CiTimeWindowTabs } from '../components/CiTimeWindowTabs';
import { CopyFormatsGroup } from '../components/CopyFormatsGroup';
import {
  OpportunityBadges,
  OpportunityCard,
  OpportunityFacts,
} from '../components/OpportunityCard';
import {
  CATEGORY_LABELS,
  activityFirst,
  buildDigest,
  buildEngagementOpportunities,
  buildInjuryOpportunities,
  buildInjuryReportOpportunity,
  buildRosterMovesOpportunity,
  buildSeasonSigningOpportunity,
  buildSigningOpportunities,
  buildTopMoveOpportunity,
  formatOpportunity,
  matchesSearch,
  rosterMoveTotals,
  selectDigest,
  sortOpportunities,
  type ContentFormat,
  type ContentOpportunity,
  type OpportunityCategory,
} from '../content-engine/opportunities';
import {
  isUsed,
  loadUsed,
  saveUsed,
  toggleUsed,
  type UsedOpportunities,
} from '../content-engine/usedOpportunities';

const FORMATS: { id: ContentFormat; label: string; hint: string }[] = [
  { id: 'short', label: 'Court', hint: 'Pour X : une ou deux phrases, 280 caractères au maximum.' },
  { id: 'social', label: 'Social', hint: 'Pour Facebook ou Instagram : titre, constat, chiffres en puces et source.' },
  { id: 'article', label: 'Article', hint: 'Base d’article avec chiffres, période et méthode.' },
  { id: 'newsletter', label: 'Infolettre', hint: 'Un paragraphe prêt à coller.' },
];

const CATEGORY_ORDER: OpportunityCategory[] = ['activity', 'signings', 'injuries', 'roster'];

const generatedAtFormat = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

interface OpportunityData {
  comparison: EngagementComparison | null;
  signingRows: SigningPlayerComparison[];
  seasonTop: TopPlayer | null;
  topDropped: TopPlayer | null;
  topInjured: TopPlayer | null;
  series: ActivityDayPoint[];
  injuryNews: RosterInjuryNews[];
  loadedAt: number;
}

type PartialFailure = 'signings' | 'series' | 'moves' | 'injuries';

const FAILURE_NOTES: Record<PartialFailure, string> = {
  signings: 'Les signatures par joueur n’ont pas pu être chargées : ces sujets sont masqués.',
  series: 'La série quotidienne n’a pas pu être chargée : les sujets du jour sont masqués.',
  moves: 'Les joueurs les plus libérés ou placés en LTIR n’ont pas pu être chargés.',
  injuries: 'Les blessures et retours de la LNH n’ont pas pu être chargés.',
};

const EMPTY: Promise<{ data: null; error: null }> = Promise.resolve({ data: null, error: null });

/**
 * Opportunités de contenu : sujets prêts à publier pour les gestionnaires de
 * médias, tirés des RPC `ci_*`. Seules une erreur d'autorisation ou l'échec de
 * la comparaison de période bloquent la page ; les autres sources manquantes
 * masquent leurs sujets avec une note.
 */
export function ContentOpportunitiesPage({ pools }: { pools: AuthorizedPool[] }) {
  const userId = useCiAuthStore((s) => s.user_id);
  const leagueId = useCiPoolStore((s) => s.selectedLeagueId);
  const [window, setWindow] = useState<TimeWindow>('7d');
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState<OpportunityData | null>(null);
  const [failures, setFailures] = useState<PartialFailure[]>([]);
  const [ownership, setOwnership] = useState<Map<number, PlayerOwnership> | null>(null);
  const [ownershipFailed, setOwnershipFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<OpportunityCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [hideUsed, setHideUsed] = useState(false);
  const [format, setFormat] = useState<ContentFormat>('social');
  const [used, setUsed] = useState<UsedOpportunities>(() => loadUsed());
  const [headlineIndex, setHeadlineIndex] = useState(0);

  useEffect(() => {
    setHeadlineIndex(0);
  }, [window, leagueId]);

  const leagueName = pools.find((pool) => pool.league_id === leagueId)?.league_name ?? null;

  useEffect(() => {
    let cancelled = false;
    if (!userId || !leagueId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      const days = TIME_WINDOW_DAYS[window];
      const [comp, signing, seasonTop, dropped, injured, activity, injuries] = await Promise.all([
        days === null ? EMPTY : fetchEngagementComparison(userId, leagueId, days),
        days === null ? EMPTY : fetchSigningPlayerComparison(userId, leagueId, days),
        days === null ? fetchTopPlayers(userId, leagueId, 'signed', null, 1) : EMPTY,
        fetchTopPlayers(userId, leagueId, 'dropped', days, 1),
        fetchTopPlayers(userId, leagueId, 'injured', days, 1),
        fetchActivityTimeseries(userId, leagueId, Math.min(windowDays(window) + 7, 400)),
        fetchRosterInjuryNews(userId, leagueId, 7),
      ]);
      if (cancelled) return;
      const all = [comp, signing, seasonTop, dropped, injured, activity, injuries];
      const denied = all.find((result) => result.error && isAuthorizationError(result.error));
      if (denied || comp.error) {
        setError(
          denied
            ? 'Accès refusé pour ce pool.'
            : 'Erreur lors du chargement des opportunités. Réessayez avec le bouton Actualiser.'
        );
        setData(null);
        setLoading(false);
        return;
      }
      const nextFailures: PartialFailure[] = [];
      if (signing.error || seasonTop.error) nextFailures.push('signings');
      if (activity.error) nextFailures.push('series');
      if (dropped.error || injured.error) nextFailures.push('moves');
      if (injuries.error) nextFailures.push('injuries');
      setFailures(nextFailures);
      setData({
        comparison: comp.data,
        signingRows: signing.data ?? [],
        seasonTop: seasonTop.data?.[0] ?? null,
        topDropped: dropped.data?.[0] ?? null,
        topInjured: injured.data?.[0] ?? null,
        series: activity.data ?? [],
        injuryNews: injuries.data ?? [],
        loadedAt: Date.now(),
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, leagueId, window, reloadKey]);

  // Sujets sans la détention : servent à connaître les joueurs à interroger.
  const baseOpportunities = useMemo(
    () => (data ? buildAll(data, { window, leagueName }) : []),
    [data, window, leagueName]
  );
  const playerIdsKey = useMemo(
    () =>
      [...new Set(baseOpportunities.flatMap((o) => (o.playerId === null ? [] : [o.playerId])))]
        .sort((a, b) => a - b)
        .join(','),
    [baseOpportunities]
  );

  useEffect(() => {
    let cancelled = false;
    setOwnership(null);
    setOwnershipFailed(false);
    if (!userId || !leagueId || !playerIdsKey) return;
    (async () => {
      const ids = playerIdsKey.split(',').map(Number);
      const result = await fetchPlayerOwnership(userId, leagueId, ids);
      if (cancelled) return;
      if (result.error) {
        // Complément facultatif (RPC peut-être pas encore installée) : on garde les sujets.
        setOwnershipFailed(true);
        return;
      }
      setOwnership(new Map((result.data ?? []).map((row) => [row.player_id, row])));
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, leagueId, playerIdsKey]);

  const opportunities = useMemo(
    () =>
      data
        ? sortOpportunities(buildAll(data, { window, leagueName, ownership: ownership ?? undefined }))
        : [],
    [data, window, leagueName, ownership]
  );

  const isOpportunityUsed = (opportunity: ContentOpportunity) =>
    leagueId ? isUsed(used, leagueId, opportunity.id) : false;
  const available = opportunities.filter((opportunity) => !isOpportunityUsed(opportunity));
  // Un sujet marqué comme utilisé sort de la liste : le suivant prend sa place.
  const headlinePosition = Math.min(headlineIndex, Math.max(0, available.length - 1));
  const headline = available[headlinePosition] ?? null;
  const digestItems = selectDigest(available);
  const digestContext = { window, leagueName };

  // Dans « Tout », l'activité du pool passe toujours en premier.
  const filtered = activityFirst(opportunities).filter(
    (opportunity) =>
      (category === 'all' || opportunity.category === category) &&
      (!hideUsed || !isOpportunityUsed(opportunity)) &&
      matchesSearch(opportunity, search)
  );
  const categoryCounts = CATEGORY_ORDER.map((id) => ({
    id,
    count: opportunities.filter((opportunity) => opportunity.category === id).length,
  }));
  const activeFormat = FORMATS.find((item) => item.id === format) ?? FORMATS[0];

  const onToggleUsed = (opportunity: ContentOpportunity) => {
    if (!leagueId) return;
    const next = toggleUsed(used, leagueId, opportunity.id);
    setUsed(next);
    saveUsed(next);
  };

  if (!leagueId) {
    return <div className="ci-empty">Sélectionnez un pool pour afficher les opportunités.</div>;
  }
  if (error) {
    return (
      <div className="ci-error">
        {error}{' '}
        <button type="button" className="ci-btn ci-btn--ghost ci-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
          Actualiser
        </button>
      </div>
    );
  }
  if (!data) return <div className="ci-empty">{loading ? 'Chargement…' : 'Aucune donnée pour ce pool.'}</div>;

  const generatedAt = data.comparison?.generated_at ?? new Date(data.loadedAt).toISOString();
  const poolLabel = leagueName ? `le pool ${leagueName}` : 'ce pool';
  const notes = [
    ...failures.map((failure) => FAILURE_NOTES[failure]),
    ...(ownershipFailed
      ? ['Le nombre d’équipes qui ont chaque joueur n’est pas disponible : les sujets s’affichent sans ce chiffre.']
      : []),
  ];

  return (
    <div className={loading ? 'ci-overview is-refreshing' : 'ci-overview'} aria-busy={loading}>
      <div className="ci-page-head">
        <div>
          <h1 className="ci-page-head__title">Opportunités de contenu</h1>
          <p className="ci-page-head__meta">
            {available.length} sujet{available.length > 1 ? 's' : ''} prêt{available.length > 1 ? 's' : ''} pour{' '}
            {poolLabel} · {PERIOD_PHRASES[window].current} · données au{' '}
            {generatedAtFormat.format(new Date(generatedAt))}
          </p>
        </div>
        <div className="ci-page-head__controls">
          <CiTimeWindowTabs value={window} onChange={setWindow} />
          <button
            type="button"
            className="ci-btn ci-btn--ghost ci-btn--sm"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
          >
            {loading ? 'Actualisation…' : 'Actualiser'}
          </button>
        </div>
      </div>

      {notes.length > 0 && (
        <div className="ci-info" role="status">
          <ul className="ci-info__list">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {headline ? (
        <div className="ci-story-grid">
          <section className="ci-section ci-headline" aria-labelledby="ci-headline-title">
            <div className="ci-headline__head">
              <h2 id="ci-headline-title" className="ci-story__eyebrow">À la une</h2>
              {available.length > 1 && (
                <div className="ci-headline__nav" role="group" aria-label="Parcourir les sujets à la une">
                  <button
                    type="button"
                    className="ci-btn ci-btn--ghost ci-btn--sm"
                    onClick={() => setHeadlineIndex(headlinePosition - 1)}
                    disabled={headlinePosition === 0}
                  >
                    ‹ Précédent
                  </button>
                  <span className="ci-headline__position" aria-live="polite">
                    {headlinePosition + 1} sur {available.length}
                  </span>
                  <button
                    type="button"
                    className="ci-btn ci-btn--ghost ci-btn--sm"
                    onClick={() => setHeadlineIndex(headlinePosition + 1)}
                    disabled={headlinePosition >= available.length - 1}
                  >
                    Suivant ›
                  </button>
                </div>
              )}
            </div>
            <OpportunityBadges opportunity={headline} used={false} />
            <p className="ci-story__headline">{headline.title}</p>
            <p className="ci-opp-card__reason">{headline.reason}</p>
            {headline.angle && <p className="ci-opp-card__angle">{headline.angle}</p>}
            <OpportunityFacts opportunity={headline} />
            <CopyFormatsGroup
              key={headline.id}
              formats={FORMATS}
              getText={(id) => formatOpportunity(headline, id, leagueName)}
              groupLabel="Copier le sujet à la une"
              copyAriaLabel={(label) => `Copier le sujet à la une au format ${label}`}
              buttonClassName="ci-btn ci-btn--sm"
            >
              <button
                type="button"
                className="ci-btn ci-btn--ghost ci-btn--sm"
                onClick={() => onToggleUsed(headline)}
              >
                Marquer comme utilisé
              </button>
            </CopyFormatsGroup>
          </section>
          {digestItems.length >= 2 && (
            <section className="ci-section ci-digest" aria-labelledby="ci-digest-title">
              <h2 id="ci-digest-title" className="ci-story__eyebrow">Bulletin de la période</h2>
              <p className="ci-section__meta">
                Les {digestItems.length} sujets les plus forts, un seul par joueur, en un seul texte.
              </p>
              <ol className="ci-digest__list">
                {digestItems.map((item) => (
                  <li key={item.id}>{item.title}</li>
                ))}
              </ol>
              <CopyFormatsGroup
                formats={FORMATS}
                getText={(id) => buildDigest(available, id, digestContext)}
                groupLabel="Copier le bulletin"
                copyAriaLabel={(label) => `Copier le bulletin au format ${label}`}
                buttonClassName="ci-btn ci-btn--ghost ci-btn--sm"
              />
            </section>
          )}
        </div>
      ) : (
        <section className="ci-section">
          <div className="ci-empty">
            {opportunities.length
              ? 'Tous les sujets de cette période sont marqués comme utilisés.'
              : 'Aucun sujet détecté pour cette période. Essayez une période plus longue.'}
          </div>
        </section>
      )}

      {opportunities.length > 0 && (
        <section className="ci-section" aria-labelledby="ci-all-title">
          <div className="ci-section__header">
            <h2 id="ci-all-title" className="ci-section__title">Tous les sujets</h2>
            <span className="ci-opportunity-filter__count">
              {filtered.length === 0
                ? 'Aucun sujet affiché'
                : `${filtered.length} sur ${opportunities.length} affiché${filtered.length > 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="ci-opp-toolbar">
            <div className="ci-tabs ci-category-tabs" role="group" aria-label="Filtrer par catégorie">
              <button
                type="button"
                aria-pressed={category === 'all'}
                className={`ci-tabs__btn${category === 'all' ? ' is-active' : ''}`}
                onClick={() => setCategory('all')}
              >
                Tout ({opportunities.length})
              </button>
              {categoryCounts.map(({ id, count }) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={category === id}
                  disabled={count === 0}
                  className={`ci-tabs__btn${category === id ? ' is-active' : ''}`}
                  onClick={() => setCategory(id)}
                >
                  {CATEGORY_LABELS[id]} ({count})
                </button>
              ))}
            </div>
            <div className="ci-opp-toolbar__row">
              <input
                type="search"
                className="ci-input ci-opp-toolbar__search"
                placeholder="Rechercher un joueur"
                aria-label="Rechercher un joueur ou un sujet"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <label className="ci-checkbox">
                <input
                  type="checkbox"
                  checked={hideUsed}
                  onChange={(event) => setHideUsed(event.target.checked)}
                />
                Masquer les sujets déjà utilisés
              </label>
            </div>
            <div className="ci-opp-toolbar__row">
              <div className="ci-tabs" role="group" aria-label="Format du texte">
                {FORMATS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={format === item.id}
                    className={`ci-tabs__btn${format === item.id ? ' is-active' : ''}`}
                    onClick={() => setFormat(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <span className="ci-section__meta ci-section__meta--tight">{activeFormat.hint}</span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="ci-empty">Aucun sujet ne correspond à ces filtres.</div>
          ) : (
            <div className="ci-opp-grid">
              {filtered.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  format={format}
                  formatLabel={activeFormat.label}
                  leagueName={leagueName}
                  used={isOpportunityUsed(opportunity)}
                  onToggleUsed={onToggleUsed}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <details className="ci-section ci-details">
        <summary>Sources et limites des données</summary>
        <ul className="ci-data-limits">
          <li>Chaque texte reprend uniquement des chiffres du pool : aucune génération par IA, aucune projection.</li>
          <li>Les signatures comptées sont celles d’agents libres.</li>
          <li>
            Les blessures et retours de la LNH (7 derniers jours) ne sont retenus que si le joueur est
            actuellement dans au moins une équipe du pool.
          </li>
          <li>
            Les libérations et mouvements LTIR sont suivis depuis le {ROSTER_MOVES_TRACKED_SINCE} seulement :
            pas de comparaison avec une période antérieure.
          </li>
          <li>
            « Équipes qui l’ont » compte les équipes du pool qui ont le joueur aujourd’hui (alignement,
            réserve ou LTIR), pas au moment de l’événement.
          </li>
          <li>La période « Saison » n’a pas de période précédente : pas de hausse ni de baisse.</li>
          <li>Les sujets marqués comme utilisés sont mémorisés dans ce navigateur pendant 30 jours.</li>
        </ul>
      </details>
    </div>
  );
}

function buildAll(
  data: OpportunityData,
  ctx: Parameters<typeof buildInjuryOpportunities>[1]
): ContentOpportunity[] {
  const comparable = data.comparison?.previous_complete !== false;
  return [
    ...(ctx.window === 'season'
      ? buildSeasonSigningOpportunity(data.seasonTop, ctx)
      : buildSigningOpportunities(data.signingRows, ctx, comparable)),
    ...buildTopMoveOpportunity(data.topDropped, 'dropped', ctx),
    ...buildTopMoveOpportunity(data.topInjured, 'injured', ctx),
    ...buildRosterMovesOpportunity(rosterMoveTotals(data.comparison, data.series, ctx.window), ctx),
    ...buildInjuryReportOpportunity(data.injuryNews, ctx),
    ...buildInjuryOpportunities(data.injuryNews, ctx),
    ...buildEngagementOpportunities(data.comparison, data.series, ctx),
  ];
}
